import { assignOptimally, findMaxCount } from "~/_legacy/utils/algorithm";

export const SLOT_SIZE = { width: 232, height: 144 };
const PADDING = { top: 10, bottom: 10, left: 10, right: 46 };
const MIN_GAP = 10;

export type ConnectingType = ["left" | "center" | "right", "top" | "center" | "bottom"];

export interface SlotPoint {
  /** 슬롯 박스의 좌상단 (viewport 좌표) */
  point: { x: number; y: number };
  /** 카메라 → 슬롯 연결선 끝점 (slot box의 변 중간) */
  connectingPoint: { x: number; y: number };
  connectingType: ConnectingType;
  slotOrder: number;
}

/** canvas 뷰포트 크기 기준으로 외곽에 슬롯 위치 계산 (legacy getSlotPoints 포팅) */
export function computeSlotPoints(viewportWidth: number, viewportHeight: number): SlotPoint[] {
  const { width: sw, height: sh } = SLOT_SIZE;

  const horizontalCount = findMaxCount(viewportWidth - PADDING.left - PADDING.right, sw, MIN_GAP);
  const verticalCount = findMaxCount(viewportHeight - PADDING.top - PADDING.bottom, sh, MIN_GAP);

  if (horizontalCount === 0 || verticalCount === 0) return [];

  const horizontalGap =
    horizontalCount > 1
      ? (viewportWidth - PADDING.left - PADDING.right - sw * horizontalCount) /
        (horizontalCount - 1)
      : 0;
  const verticalGap =
    verticalCount > 1
      ? (viewportHeight - PADDING.top - PADDING.bottom - sh * verticalCount) / (verticalCount - 1)
      : 0;

  const result: SlotPoint[] = [];
  let slotOrder = 0;

  for (let i = 0; i < horizontalCount; i += 1) {
    for (let j = 0; j < verticalCount; j += 1) {
      // 외곽만 (왼쪽 열, 맨위 행, 맨아래 행)
      if (!(i === 0 || j === 0 || j === verticalCount - 1)) continue;

      const point = {
        x: PADDING.left + i * (sw + horizontalGap),
        y: PADDING.top + j * (sh + verticalGap),
      };

      const connecting = { x: point.x, y: point.y };
      const connectingType: ConnectingType = ["center", "center"];

      if (i === 0) {
        connectingType[0] = "right";
        connecting.x += sw;
      } else {
        connecting.x += sw / 2;
      }

      if (j === 0) {
        connectingType[1] = "bottom";
        connecting.y += sh;
      } else if (j === verticalCount - 1) {
        connectingType[1] = "top";
      } else {
        connecting.y += sh / 2;
      }

      result.push({ point, connectingPoint: connecting, connectingType, slotOrder });
      slotOrder += 1;
    }
  }

  return result;
}

/** 카메라들을 가장 가까운 슬롯에 배정 (legacy assignOptimally 그대로 사용) */
export function assignCamerasToSlots(
  cameraPoints: { x: number; y: number }[],
  slotPoints: SlotPoint[],
): number[] {
  const max = Math.min(cameraPoints.length, slotPoints.length);
  const points = cameraPoints.slice(0, max);
  return assignOptimally(
    points as Parameters<typeof assignOptimally>[0],
    slotPoints.slice(0, slotPoints.length).map((s) => s.connectingPoint) as Parameters<typeof assignOptimally>[1],
  );
}
